import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listUserSurveys } from "../../api/api";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import SignedOutPrompt from "../SignedOutPrompt";
import SurveyProgress from "../survey/SurveyProgress";

const HUB_GROUPS = [
  { id: "general", label: "General" },
  { id: "new", label: "New" },
  { id: "complete", label: "Complete" },
];

/**
 * Hub section for a one-shot or retakeable survey.
 *
 * @param {object} survey
 * @returns {"general"|"new"|"complete"}
 */
function hubGroupId(survey) {
  if (survey.allow_retake) return "general";
  if (survey.status === "submitted") return "complete";
  return "new";
}

/**
 * Beta feedback hub: list of surveys with completed or in-progress state.
 *
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
export default function BetaHubPage({ userId, user }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      const result = await listUserSurveys(userId);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
        setSurveys([]);
      } else {
        setSurveys(result.data || []);
      }
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const grouped = useMemo(() => {
    const buckets = { general: [], new: [], complete: [] };
    surveys.forEach((survey) => {
      buckets[hubGroupId(survey)].push(survey);
    });
    return buckets;
  }, [surveys]);

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Beta feedback"
        description="Sign in to complete beta surveys and help shape Assembly."
      />
    );
  }

  if (user?.type === "organization") {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Beta feedback"
        description="Beta surveys are available on personal accounts."
      />
    );
  }

  return (
    <div className="bg-ui-background min-h-[calc(100dvh-3.5rem)] w-full">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        <h1 className="font-display text-3xl font-medium text-text-primary">Beta testing feedback</h1>
        <p className="mt-2 text-text-secondary">
          These short surveys help us decide what to build next. Your answers save as you go. You can
          leave and pick up later.
        </p>

        {error ? (
          <p className="mt-6 text-sm text-error" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="mt-8 text-sm text-text-muted">Loading surveys…</p>
        ) : (
          <div className="mt-8 flex flex-col gap-8">
            {HUB_GROUPS.map((group) => (
              <SurveyGroup
                key={group.id}
                label={group.label}
                surveys={grouped[group.id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Labeled survey section with a dividing line. Hidden when empty.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {object[]} props.surveys
 * @returns {JSX.Element|null}
 */
function SurveyGroup({ label, surveys }) {
  if (!surveys.length) return null;
  return (
    <section>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="shrink-0 text-sm font-semibold uppercase tracking-wide text-text-muted">
          {label}
        </h2>
        <Separator className="w-auto flex-1" />
      </div>
      <ul className="flex flex-col gap-4">
        {surveys.map((survey) => (
          <li key={survey.id}>
            <HubSurveyCard survey={survey} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * @param {Object} props
 * @param {object} props.survey
 * @returns {JSX.Element}
 */
function HubSurveyCard({ survey }) {
  const total = Number(survey.total) || 0;
  const answered = Number(survey.answered) || 0;
  const groupId = hubGroupId(survey);
  const showNew = groupId === "new";
  const showCompleted = groupId === "complete";
  const showProgress =
    survey.status === "in_progress" ||
    (!survey.allow_retake && survey.status !== "submitted" && answered > 0);

  return (
    <Link to={`/beta/${survey.id}`} className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring">
      <Card className="overflow-hidden border-ui-border transition-colors hover:bg-ui-muted/40">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-lg text-text-primary">{survey.title}</CardTitle>
            {showNew ? <Badge variant="default">New</Badge> : null}
            {showCompleted ? <Badge variant="accent">Completed</Badge> : null}
          </div>
          {survey.description ? (
            <CardDescription>{survey.description}</CardDescription>
          ) : null}
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-muted">
            {survey.estimated_minutes ? (
              <span>About {survey.estimated_minutes} min</span>
            ) : null}
            {survey.allow_retake ? <span>You can submit more than once</span> : null}
            {survey.allow_edit && !survey.allow_retake ? <span>Answers can be edited</span> : null}
          </div>
          {showProgress ? (
            <p className="mt-3 text-xs text-text-secondary">In progress</p>
          ) : null}
        </CardContent>
        {showProgress ? <SurveyProgress answered={answered} total={total} /> : null}
      </Card>
    </Link>
  );
}
